import { usePageTitle } from "../hooks/usePageTitle";
import { ArrowRight, Calendar, Mail, Clock, Star } from "lucide-react";
import { CALCOM_DEMO_URL } from "../config/demo";

/* ── Jake M. testimonial ────────────────────────────────── */
const JAKE = {
  quote: "I booked 3 jobs in the first week that I know I would have lost before. The follow-up alone paid for itself.",
  name: "Jake M.",
  business: "Jake's Exterior Services",
  img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
};

export default function ContactPage() {
  usePageTitle("Contact | CleanBid.ai");

  return (
    <div className="bg-[#0A0A0F] pt-20 min-h-screen" data-testid="contact-page">

      {/* ── Page Header ── */}
      <section className="py-16 md:py-20 border-b border-[#1E1E2E]">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#71DED6]">
            Get in touch
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-[#F4F4F8] mt-3 mb-4">
            We'd love to hear from you.
          </h1>
          <p className="text-lg text-[#8B8BA7] max-w-xl leading-relaxed">
            Whether you have a question about pricing, a partnership idea, or just want to learn more — we're real people and we respond fast.
          </p>
        </div>
      </section>

      {/* ── Two-column body ── */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">

            {/* ── LEFT: Primary actions (no form) ── */}
            <div className="lg:col-span-3">
              <div className="bg-[#111118] border border-[#1E1E2E] rounded-3xl p-8 md:p-10">
                <h2 className="text-2xl font-bold text-[#F4F4F8] mb-2">
                  Let's talk about your business.
                </h2>
                <p className="text-[#8B8BA7] leading-relaxed mb-8">
                  Book a live 20-minute walk-through, or send us an email — whichever's easier. We'll get back to you within 1 business day.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Primary: Schedule a demo */}
                  <a
                    href={CALCOM_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-schedule-demo-button"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#71DED6] text-[#10151D] font-semibold px-6 py-3.5 rounded-xl text-sm hover:scale-[1.01] hover:shadow-[0_0_20px_rgba(113,222,214,0.3)] transition-all duration-200"
                  >
                    <Calendar className="w-4 h-4" />
                    Schedule a Demo
                  </a>

                  {/* Secondary: Email us */}
                  <a
                    href="mailto:info@cleanbid.ai"
                    data-testid="contact-email-us-button"
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-white/20 text-[#F4F4F8] font-semibold px-6 py-3.5 rounded-xl text-sm hover:bg-white/5 transition-colors duration-200"
                  >
                    <Mail className="w-4 h-4" />
                    Email Us
                  </a>
                </div>

                <p className="text-[#5A5A7A] text-xs mt-6">
                  Prefer email? Reach us any time at{" "}
                  <a
                    href="mailto:info@cleanbid.ai"
                    data-testid="contact-email-inline"
                    className="text-[#71DED6] hover:underline"
                  >
                    info@cleanbid.ai
                  </a>
                  .
                </p>
              </div>
            </div>

            {/* ── RIGHT: Info ── */}
            <div className="lg:col-span-2 space-y-8">

              {/* Book a call */}
              <div className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-6 space-y-3">
                <div className="flex items-center gap-2.5">
                  <Calendar className="w-4 h-4 text-[#71DED6]" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#8B8BA7]">
                    Rather book a call?
                  </span>
                </div>
                <p className="text-sm text-[#8B8BA7] leading-relaxed">
                  Skip the back-and-forth — get a live 20-minute walk-through of CleanBid tailored to your business.
                </p>
                <a
                  href={CALCOM_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-book-demo-link"
                  className="inline-flex items-center gap-2 bg-[#71DED6] text-[#10151D] font-semibold px-5 py-2.5 rounded-full text-sm hover:scale-[1.02] transition-transform duration-200"
                >
                  Book a Demo
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* Email */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#71DED6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-4 h-4 text-[#71DED6]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#8B8BA7] mb-1">
                    Email us directly
                  </p>
                  <a
                    href="mailto:info@cleanbid.ai"
                    data-testid="contact-email-link"
                    className="text-[#F4F4F8] text-sm hover:text-[#71DED6] transition-colors duration-200"
                  >
                    info@cleanbid.ai
                  </a>
                </div>
              </div>

              {/* Response time */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#71DED6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="w-4 h-4 text-[#71DED6]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#8B8BA7] mb-1">
                    Response time
                  </p>
                  <p className="text-[#F4F4F8] text-sm">
                    We respond to all messages within 1 business day.
                  </p>
                </div>
              </div>

              {/* Testimonial */}
              <div
                className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-6"
                data-testid="contact-testimonial"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-[#71DED6] text-[#71DED6]" />
                  ))}
                </div>
                <blockquote className="text-[#8B8BA7] text-sm leading-relaxed mb-5">
                  "{JAKE.quote}"
                </blockquote>
                <div className="flex items-center gap-3">
                  <img
                    src={JAKE.img}
                    alt={JAKE.name}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-[#F4F4F8] text-sm font-semibold">{JAKE.name}</p>
                    <p className="text-[#5A5A7A] text-xs">{JAKE.business}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
